<template>
	<div class="pt-2">
		<CommonFlame :title="title">
			<template v-slot:app-bar-btn> </template>
			<ComponentShippingSearchSection :query="q" @search-shippings="searchShippings($event)" />
			<v-divider class="mt-4" />
			<h6 class="text-body-1 font-weight-bold mt-2">予定出庫一覧</h6>
			<div class="table-container mt-4">
				<v-data-table
					locale="ja-JP"
					v-model="selectedShippings"
					:items="shippings"
					:headers="headers"
					:mobile-breakpoint="240"
					show-select
					hide-default-footer
					disable-filtering
					disable-pagination
					disable-sort
					no-data-text="データがありません。"
					item-class="no-click"
				>
					<template v-slot:[`item.start_date`]="{ item }">
						<td style="white-space: pre-line">{{ item.start_date }}</td>
					</template>
					<template v-slot:[`item.shipping_date`]="{ item }">
						<td style="white-space: pre-line">{{ item.shipping_date }}</td>
					</template>
				</v-data-table>
			</div>
			<PaginationModule :pagination="pagination" @get-page="getPage($event)"></PaginationModule>
			<template v-if="selectedShippings.length">
				<v-divider class="mt-4" />
				<h6 class="text-body-1 font-weight-bold mt-2">出庫個数設定</h6>
				<v-form ref="form" v-model="valid">
					<v-row no-gutters>
						<v-col
							v-for="shipping in selectedShippings"
							:key="shipping.id"
							cols="12"
							sm="6"
							class="mt-4 p-auto-1"
						>
							<v-text-field
								v-model="shipping.actual_quantity"
								:label="`${shipping.order_target.component.name}の出庫数 (${shipping.order_target.ordered_product.product_number})`"
								outlined
								dense
								type="number"
								persistent-hint
								:rules="[rules.required]"
								hide-details
							/>
							<span style="font-size: 0.85em;">{{'予定出庫個数は' + shipping.planned_quantity + 'です。'}}</span>
						</v-col>
					</v-row>
					<v-row no-gutters justify="center" class="mt-2">
						<v-btn
							color="secondary"
							@click="checkShippingQuantity()"
							:disabled="!valid || !isAllSelectedQuantityNonZero"
						>
							<v-icon class="mr-2">mdi-check</v-icon>
							出庫を確定する
						</v-btn>
					</v-row>
				</v-form>
			</template>
		</CommonFlame>
		<ComponentConfirmationDialog
			:dialogContent="dialogContent"
			:exceededShippings="exceededShippings"
			:dialog.sync="dialog"
			:dialogTitle="dialogTitle"
			@submit="submitForm"
		/>
	</div>
</template>

<script>
import axios from "axios"
import qs from "qs"
import moment from "moment"
import authenticate from "../../helper/authenticate"
import errorHandling from "../../helper/errorHandling"
import common from "../../helper/common"
import rules from "../../helper/rules"
import CommonFlame from "../shared/CommonFlame.vue"
import PaginationModule from "../../modules/PaginationModule.vue"
import ComponentShippingSearchSection from "./components/ComponentShippingSearchSection.vue"
import ComponentConfirmationDialog from "./components/ComponentConfirmationDialog.vue"

export default {
	components: {
		CommonFlame,
		PaginationModule,
		ComponentShippingSearchSection,
		ComponentConfirmationDialog,
	},
	data() {
		return {
			title: "部材出庫",
			shippings: [],
			selectedShippings: [],
			authority_flag: false,
			headers: headers(),
			pagination: {
				count: null,
				current: 1,
				limit_value: null,
				next: null,
				pages: null,
				previous: null,
			},
			q: {
				order_component_shipping_date_lteq: null,
				order_component_shipping_date_gteq: null,
				order_component_start_date_lteq: null,
				order_component_start_date_gteq: null,
				order_component_product_number_or_name_cont: null,
			},
			valid: false,
			common,
			rules,
			moment,
			dialogTitle: "確認",
			dialog: false,
			dialogContent: null,
			exceededShippings: [],
		}
	},

	mounted() {
		const page = this.$route.query.page ? this.$route.query.page : 1
		this.setQuery()
		this.fetchShippings(this.q, page, true)
	},
	methods: {
		checkShippingQuantity() {
			const shippings = this.selectedShippings.filter((shipping) => {
				return shipping.planned_quantity < shipping.actual_quantity
			})
			if (shippings.length) {
				this.exceededShippings = shippings
				this.dialog = true
				this.dialogContent = "出庫数が予定出庫数を超えているものがあります"
			} else {
				this.submitForm()
			}
		},
		fetchShippings(q, page, first_visit = false) {
			const headers = authenticate.setHeader()
			const params = { page, q, first_visit }
			const paramsSerializer = (params) => qs.stringify(params, { arrayFormat: "brackets" })

			axios
				.get("/erp/v1/components/shippings", {
					params,
					paramsSerializer,
					headers,
				})
				.then((response) => {
					this.shippings = this.setShippings(response.data.shippings)
					this.pagination = response.data.pagination
					// 初回のアクセスのみデータを取得する
					if (first_visit) {
						this.authority_flag = response.data.authority_flag
					}
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
		},
		setShippings(shippings) {
			return shippings.map((shipping) => {
				shipping.product_name = `${shipping.order_target.ordered_product.product.name} ${
					shipping.order_target.ordered_product.sub_name
						? shipping.order_target.ordered_product.sub_name
						: ""
				}`
				shipping.order_target.component.wildcard_symbol =
					shipping.order_target.component.wildcard == true ? "◯" : "×"
				shipping.planned_operation_date = moment(shipping.planned_operation_date).format(
					"YYYY/MM/DD"
				)
				const start_date = this.groupedDates(
					shipping.order_target.ordered_product.order_individual_infos,
					"start_date"
				) // start_dateを整形する
				shipping.start_date = start_date.join("\n")
				const shipping_date = this.groupedDates(
					shipping.order_target.ordered_product.order_individual_infos,
					"delivery_info"
				) // shipping_dateを整形する
				shipping.shipping_date = shipping_date.join("\n")
				return shipping
			})
		},
		groupedDates(array, dateKey) {
			const keys = dateKey.split(".")
			return array.reduce((groups, item) => {
				keys.forEach((key) => {
					if (key == "delivery_info" && item[key]) {
						item = item[key]["planned_shipping_date"]
					} else {
						item = item[key]
					}
				})
				if (!item) return groups
				const date = moment(item).format("YYYY/MM/DD")
				if (!groups.includes(date)) groups.push(date)
				return groups
			}, [])
		},
		submitForm() {
			const headers = authenticate.setHeader()
			const shippings = this.selectedShippings.map((shipping) => {

				return {
					id: shipping.id,
					actual_quantity: shipping.input_actual_quantity
						? shipping.actual_quantity - shipping.input_actual_quantity
						: shipping.actual_quantity,
				}
			})
			axios
				.put(
					`/erp/v1/components/shippings`,
					{ shippings: shippings },
					{
						headers,
					}
				)
				.then((res) => {
					const hasIsChanged = this.selectedShippings.some(
						(shipping) => typeof shipping.isChanged !== "undefined"
					)
					const allHaveIsChanged = this.selectedShippings.every(
						(shipping) => typeof shipping.isChanged !== "undefined"
					)
					let message
					if (hasIsChanged) {
						if (allHaveIsChanged) {
							// すべての要素がisChanged キーを持っている場合
							message = "更新"
						} else {
							// 最低でも1つの要素がisChanged キーを持っている場合
							message = "作成・更新"
						}
					} else {
						// どの要素もisChanged キーを持っていない場合
						message = "作成"
					}
					this.$store.dispatch("setFlash", {
						type: "notice",
						text: `実出庫データを${message}しました。`,
					})

					this.selectedShippings.forEach((shipping) => {
						shipping.input_actual_quantity = shipping.actual_quantity
						shipping.isChanged = true
					})
				})
				.catch((err) => {
					if (Array.isArray(err.response.data)) {
						this.$store.dispatch("setFlash", {
							type: "alert",
							text: `${err.response.data}`,
						})
					}
				})
		},
		getPage(page) {
			this.fetchShippings(this.q, page)
			this.$router.push({
				name: this.$route.name,
				query: { page: page, ...this.q },
			})
			window.scrollTo({
				top: 0,
				behavior: "smooth",
			})
		},
		searchShippings(q) {
			this.q = q
			this.fetchShippings(q, 1)
			this.$router.push({
				name: this.$route.name,
				query: { ...this.q, page: 1 },
			})
		},
		setQuery() {
			const query = this.$route.query
			let q = this.q
			Object.keys(this.$route.query).forEach((query_key) => {
				if (query[query_key] && query_key != "page") {
					q[query_key] = this.formatQuery(query[query_key])
				}
			})
			this.q = q
		},
		formatQuery(query) {
			if (query instanceof Array) {
				return query.map((v) => (isFinite(v) ? Number(v) : v))
			} else if (isFinite(query)) {
				return Number(query)
			} else {
				return query
			}
		},
	},
	computed: {
		isAllSelectedQuantityNonZero() {
			if (this.selectedShippings.length) {
				const result = this.selectedShippings.every((shipping) => {
					return shipping.actual_quantity > 0
				})
				return result
			}
		},
	},
}

function headers() {
	return [
		{
			text: "製造番号 ",
			value: "order_target.ordered_product.product_number",
			width: "200px",
		},
		{
			text: "製品名",
			value: "product_name",
			width: "200px",
		},
		{
			text: "製造数量",
			value: "order_target.ordered_product.quantity",
			width: "80px",
		},
		{
			text: "部品名",
			value: "order_target.component.name",
			width: "200px",
		},
		{
			text: "予定出庫数量",
			value: "planned_quantity",
			width: "100px",
		},
		{
			text: "出庫予定日",
			value: "planned_operation_date",
			width: "200px",
		},
		{
			text: "ワイルドカード",
			value: "order_target.component.wildcard_symbol",
			width: "70px",
		},
		{
			text: "着手開始日",
			value: "start_date",
			width: "200px",
		},
		{
			text: "出荷日",
			value: "shipping_date",
			width: "200px",
		},
	]
}
</script>
